import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, Pause, Video, Clock, Heart, Share2, Download, Volume2, VolumeX, Maximize, ChevronRight, Eye, ExternalLink } from 'lucide-react';
import AccessLevelBadge from './AccessLevelBadge';
import { getAccessLabel, isGated } from '../utils/accessLabels';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import trackingService from '../services/tracking';
import DownloadPaywall from './DownloadPaywall';
import ShareModal from './ShareModal';
import SignupInviteModal from './SignupInviteModal';
import AccessRequiredModal from './AccessRequiredModal';

const LatestDeepWaveVideos = ({ limit = 6, showTitle = true, className = "", columns = 2, excludeId = null, viewAllPosition = 'top' }) => {
    const gridColsClass = columns >= 4 ? 'md:grid-cols-2 xl:grid-cols-4' : 'md:grid-cols-2';
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPlaying, setCurrentPlaying] = useState(null);
    const [isMuted, setIsMuted] = useState(false);
    const [downloadPaywall, setDownloadPaywall] = useState({ isOpen: false, video: null });
    const [shareModal, setShareModal] = useState({ isOpen: false, video: null });
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'video' });
    const [accessModal, setAccessModal] = useState({ isOpen: false, video: null });
    const [videoDurations, setVideoDurations] = useState({});
    const [videoOrientations, setVideoOrientations] = useState({});
    const [currentTime, setCurrentTime] = useState(0);
    const [failedVideos, setFailedVideos] = useState(new Set());
    const { isAuthenticated, hasActiveSubscription } = useAuth();
    const navigate = useNavigate();
    const videoRefs = useRef({});

    useEffect(() => {
        fetchDeepWaveVideos();
        
        // Cleanup function
        return () => {
            Object.values(videoRefs.current).forEach(video => {
                if (video) {
                    video.pause();
                    video.src = '';
                }
            });
            trackingService.cleanup();
        };
    }, [limit, excludeId]);

    const fetchDeepWaveVideos = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch videos from The Deep Wave (user_id: 7)
            const response = await api.get(`/upload/public/video?limit=20`); // Fetch reasonable amount to filter

            if (response.data.success) {
                // Filter for only The Deep Wave's videos (user_id: 7)
                const deepWaveVideos = response.data.data.filter(video => video.user_id === 7 && video.id !== excludeId);
                // Limit to requested number
                const limitedVideos = deepWaveVideos.slice(0, limit);
                
                setVideos(limitedVideos);
            } else {
                setError('Failed to load Deep Wave videos');
            }
        } catch (err) {
            console.error('Error fetching Deep Wave videos:', err);
            setError('Unable to load videos. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handlePlayPause = (video) => {
        const videoElement = videoRefs.current[video.id];
        if (!videoElement) return;

        if (currentPlaying === video.id) {
            // Pause current video
            videoElement.pause();
            setCurrentPlaying(null);
            setCurrentTime(0);
            trackingService.stopPlayTracking(video.id);
        } else {
            // Pause all other videos
            Object.entries(videoRefs.current).forEach(([id, vid]) => {
                if (vid && id !== video.id.toString()) {
                    vid.pause();
                    trackingService.stopPlayTracking(parseInt(id));
                }
            });
            setCurrentTime(0);

            // Add time update listener for current playback position
            videoElement.addEventListener('timeupdate', () => {
                setCurrentTime(videoElement.currentTime);
            });

            // Play selected video
            setCurrentPlaying(video.id);
            videoElement.play().then(() => {
                // Start tracking with the tracking service
                trackingService.startPlayTracking(video.id, videoElement);
                
                // Update play count immediately for UI feedback
                setVideos(prevVideos =>
                    prevVideos.map(v =>
                        v.id === video.id
                            ? { ...v, play_count: (v.play_count || 0) + 1 }
                            : v
                    )
                );
            }).catch(err => {
                console.error('Video play error:', err);
                setCurrentPlaying(null);
                setFailedVideos(prev => new Set(prev).add(video.id));
            });
        }
    };

    const toggleMute = (video) => {
        const videoElement = videoRefs.current[video.id];
        if (videoElement) {
            videoElement.muted = !videoElement.muted;
            setIsMuted(videoElement.muted);
        }
    };

    const handleVideoEnded = (video) => {
        setCurrentPlaying(null);
        setCurrentTime(0);
        trackingService.stopPlayTracking(video.id);
    };

    const handleShareClick = (video) => {
        setShareModal({ isOpen: true, video });
    };

    const handleDownloadClick = (video) => {
        setDownloadPaywall({ isOpen: true, video });
    };

    const handleLike = async (video) => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'video' });
            return;
        }

        try {
            const response = await api.post(`/upload/${video.id}/like`);
            
            if (response.data.success) {
                setVideos(prev => prev.map(v => 
                    v.id === video.id 
                        ? { ...v, like_count: response.data.data.likeCount, user_has_liked: response.data.data.liked }
                        : v
                ));
            }
        } catch (err) {
            console.error('Error toggling like:', err);
        }
    };

    const formatDuration = (seconds) => {
        if (!seconds) return 'Unknown';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const mb = (bytes / (1024 * 1024)).toFixed(1);
        return `${mb} MB`;
    };

    const formatTimeAgo = (dateString) => {
        const now = new Date();
        const then = new Date(dateString);
        const diffInHours = Math.floor((now - then) / (1000 * 60 * 60));
        
        if (diffInHours < 1) return 'Just now';
        if (diffInHours < 24) return `${diffInHours}h ago`;
        
        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) return `${diffInDays}d ago`;
        
        const diffInWeeks = Math.floor(diffInDays / 7);
        if (diffInWeeks < 4) return `${diffInWeeks}w ago`;
        
        const diffInMonths = Math.floor(diffInDays / 30);
        return `${diffInMonths}mo ago`;
    };

    const openFullscreen = (video) => {
        const videoElement = videoRefs.current[video.id];
        if (videoElement && videoElement.requestFullscreen) {
            videoElement.requestFullscreen();
        }
    };

    const handleLoadedMetadata = (video) => {
        const videoElement = videoRefs.current[video.id];
        if (videoElement) {
            if (videoElement.duration) {
                setVideoDurations(prev => ({ ...prev, [video.id]: videoElement.duration }));
            }
            setVideoOrientations(prev => ({
                ...prev,
                [video.id]: videoElement.videoHeight > videoElement.videoWidth ? 'portrait' : 'landscape'
            }));
        }
    };

    const getVideoClass = (videoId) => {
        const isPortrait = videoOrientations[videoId] === 'portrait';
        return isPortrait
            ? 'w-full object-contain sm:max-h-[600px]'
            : 'w-full max-h-[300px] sm:max-h-[400px] lg:max-h-[600px] object-contain';
    };

    const formatTime = (seconds) => {
        if (!seconds || seconds < 0) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (loading) {
        return (
            <div className={`${className}`}>
                {showTitle && (
                    <div className="mb-6">
                        <div className="flex items-center gap-3">
                            <Video className="w-6 h-6 text-blue-400" />
                            <h2 className="text-xl sm:text-2xl font-bold gradient-text">Latest Videos by The Deep Wave</h2>
                        </div>
                    </div>
                )}
                <div className={`grid grid-cols-1 ${gridColsClass} gap-4`}>
                    {[...Array(limit)].map((_, index) => (
                        <div key={index} className="glass-card animate-pulse">
                            <div className="aspect-video bg-gray-700"></div>
                            <div className="p-4">
                                <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
                                <div className="h-3 bg-gray-700 rounded w-1/2"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`${className}`}>
                {showTitle && (
                    <div className="mb-6">
                        <div className="flex items-center gap-3">
                            <Video className="w-6 h-6 text-blue-400" />
                            <h2 className="text-xl sm:text-2xl font-bold gradient-text">Latest Videos by The Deep Wave</h2>
                        </div>
                    </div>
                )}
                <div className="text-center py-8">
                    <div className="text-4xl mb-2">🎬</div>
                    <p className="text-gray-400">{error}</p>
                </div>
            </div>
        );
    }

    if (videos.length === 0) {
        return (
            <div className={`${className}`}>
                {showTitle && (
                    <div className="mb-6">
                        <div className="flex items-center gap-3">
                            <Video className="w-6 h-6 text-blue-400" />
                            <h2 className="text-xl sm:text-2xl font-bold gradient-text">Latest Videos by The Deep Wave</h2>
                        </div>
                    </div>
                )}
                <div className="text-center py-8">
                    <div className="text-4xl mb-2">🎬</div>
                    <p className="text-gray-400">No videos yet</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`${className}`}>
            {showTitle && (
                <div className="mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                            <div className="flex items-center gap-3">
                                <Video className="w-6 h-6 text-blue-400" />
                                <h2 className="text-xl sm:text-2xl font-bold gradient-text">Latest Videos by The Deep Wave</h2>
                            </div>
                            <span className="text-xs bg-blue-600/20 text-blue-400 px-2 py-1 rounded-full self-start w-fit">
                                Official
                            </span>
                        </div>
                        {viewAllPosition === 'top' && (
                            <Link
                                to="/feed/artist/TheDeepWave/video"
                                className="text-blue-400 hover:text-blue-300 transition-colors text-sm font-medium self-start sm:self-auto"
                            >
                                View All →
                            </Link>
                        )}
                    </div>
                </div>
            )}

            <div className={`grid grid-cols-1 ${gridColsClass} gap-6`}>
                {videos.map((video, index) => (
                    <div 
                        key={video.id} 
                        className="glass-card hover:transform hover:scale-105 transition-all duration-300 group"
                    >
                        {/* Video Player / Protected Thumbnail */}
                        {isGated(video.access_level, isAuthenticated, hasActiveSubscription) ? (
                            <div className="relative bg-gray-900 overflow-hidden">
                                <video
                                    src={video.file_url}
                                    poster={video.cover_image_url}
                                    title={video.title || 'Video'}
                                    className={`${getVideoClass(video.id)} pointer-events-none`}
                                    preload="metadata"
                                    playsInline
                                    onLoadedMetadata={(e) => setVideoOrientations(prev => ({
                                        ...prev,
                                        [video.id]: e.target.videoHeight > e.target.videoWidth ? 'portrait' : 'landscape'
                                    }))}
                                />
                                <button
                                    onClick={() => setAccessModal({ isOpen: true, video })}
                                    aria-label="Protected content"
                                    className="absolute inset-0 flex items-end justify-center pb-3 bg-gradient-to-t from-black/70 via-black/10 to-transparent"
                                >
                                    <span className="text-white text-xs font-medium bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">
                                        {getAccessLabel(video.access_level, 'view')}
                                    </span>
                                </button>
                                <AccessLevelBadge accessLevel={video.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                    {formatDuration(video.duration)}
                                </div>
                            </div>
                        ) : failedVideos.has(video.id) ? (
                            <div
                                className="relative bg-gray-900 flex flex-col items-center justify-center text-center p-6"
                                style={{
                                    height: '220px',
                                    backgroundImage: video.cover_image_url ? `url(${video.cover_image_url})` : undefined,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }}
                            >
                                <div className="absolute inset-0 bg-black/60" />
                                <div className="relative z-10">
                                    <Video className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                    <p className="text-gray-300 text-sm">Video unavailable</p>
                                </div>
                            </div>
                        ) : (
                        <div className="relative bg-gray-900">
                            <video
                                ref={(el) => videoRefs.current[video.id] = el}
                                src={video.file_url}
                                className={getVideoClass(video.id)}
                                poster={video.cover_image_url}
                                title={video.title || 'Video'}
                                onEnded={() => handleVideoEnded(video)}
                                onLoadedMetadata={() => handleLoadedMetadata(video)}
                                onError={(e) => {
                                    console.error('Video error:', e);
                                    setFailedVideos(prev => new Set(prev).add(video.id));
                                }}
                                muted={isMuted}
                                preload="metadata"
                                playsInline
                            />

                            {/* Video Controls Overlay */}
                            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                                <button
                                    onClick={() => handlePlayPause(video)}
                                    aria-label={currentPlaying === video.id ? 'Pause' : 'Play'}
                                    className="p-4 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
                                >
                                    {currentPlaying === video.id ? (
                                        <Pause className="w-8 h-8 text-white" />
                                    ) : (
                                        <Play className="w-8 h-8 text-white ml-1" />
                                    )}
                                </button>
                            </div>

                            {/* Duration Badge */}
                            <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                {formatDuration(videoDurations[video.id] || video.duration)}
                            </div>

                            {/* Video Controls */}
                            <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                <button
                                    onClick={() => toggleMute(video)}
                                    className="p-2 bg-black/50 rounded-lg hover:bg-black/70 transition-colors"
                                    title={isMuted ? "Unmute" : "Mute"}
                                >
                                    {isMuted ? (
                                        <VolumeX className="w-4 h-4 text-white" />
                                    ) : (
                                        <Volume2 className="w-4 h-4 text-white" />
                                    )}
                                </button>
                                <button
                                    onClick={() => openFullscreen(video)}
                                    className="p-2 bg-black/50 rounded-lg hover:bg-black/70 transition-colors"
                                    title="Fullscreen"
                                >
                                    <Maximize className="w-4 h-4 text-white" />
                                </button>
                            </div>

                            {/* Playing Indicator */}
                            {currentPlaying === video.id && (
                                <div className="absolute top-2 left-2 flex items-center gap-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                    <span>{formatTime(currentTime)}</span>
                                </div>
                            )}
                        </div>
                        )}

                        {/* Video Info */}
                        <div className="p-4">
                            <h3 className="font-semibold text-white mb-2 line-clamp-2">
                                {video.title || video.original_filename}
                            </h3>

                            <div className="flex items-center gap-2 text-sm text-gray-400 mb-3">
                                <span>🌊</span>
                                <span>The Deep Wave</span>
                                <span>•</span>
                                <span>{formatTimeAgo(video.created_at)}</span>
                            </div>

                            {video.description && (
                                <p className="text-sm text-gray-400 mb-3 line-clamp-2">
                                    {video.description}
                                </p>
                            )}

                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <Clock className="w-3 h-3" />
                                        <span>{formatDuration(videoDurations[video.id] || video.duration)}</span>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <Eye className="w-3 h-3" />
                                        <span>{(video.play_count || 0).toLocaleString()}</span>
                                    </div>
                                    <span className="hidden sm:block">{formatFileSize(video.file_size)}</span>
                                </div>

                                <div className="flex items-center gap-0.5 flex-shrink-0">
                                    <button
                                        onClick={() => handleLike(video)}
                                        className={`flex items-center gap-0.5 p-1 transition-colors ${
                                            video.user_has_liked
                                                ? 'text-red-400'
                                                : 'text-gray-400 hover:text-red-400'
                                        }`}
                                        title={video.user_has_liked ? 'Liked' : 'Like'}
                                    >
                                        <Heart className={`w-3 h-3 ${video.user_has_liked ? 'fill-current' : ''}`} />
                                        <span className="text-xs hidden sm:inline">{video.like_count || 0}</span>
                                    </button>
                                    <button
                                        onClick={() => handleShareClick(video)}
                                        className="p-1 text-gray-400 hover:text-blue-400 transition-colors"
                                        title="Share"
                                    >
                                        <Share2 className="w-3 h-3" />
                                    </button>
                                    <Link
                                        to={`/view/video/${video.username}/${video.stored_filename || video.original_filename || video.id}`}
                                        className="p-1.5 text-gray-300 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-all duration-300"
                                        title="View Video Page"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                    </Link>
                                    <button
                                        onClick={() => handleDownloadClick(video)}
                                        className="p-1 text-gray-400 hover:text-green-400 transition-colors"
                                        title="Download"
                                    >
                                        <Download className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {viewAllPosition === 'bottom' && (
                <div className="mt-6 text-center">
                    <Link
                        to="/feed/artist/TheDeepWave/video"
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 hover:text-blue-300 rounded-lg transition-all duration-200 hover:scale-105 text-sm border border-blue-600/20"
                    >
                        <Video className="w-3.5 h-3.5" />
                        View All Videos
                    </Link>
                </div>
            )}

            {/* Modals */}
            <ShareModal
                isOpen={shareModal.isOpen}
                onClose={() => setShareModal({ isOpen: false, video: null })}
                mediaTitle={shareModal.video?.title || shareModal.video?.original_filename || 'Unknown Video'}
                mediaType="video"
                username={shareModal.video?.username}
                mediaId={shareModal.video?.stored_filename || shareModal.video?.original_filename || shareModal.video?.id}
                description={shareModal.video?.description}
                artistName={shareModal.video?.display_name || shareModal.video?.username}
                imageUrl={shareModal.video?.cover_image_url}
                duration={shareModal.video?.duration ? formatDuration(shareModal.video.duration) : null}
            />
            
            <DownloadPaywall
                isOpen={downloadPaywall.isOpen}
                onClose={() => setDownloadPaywall({ isOpen: false, video: null })}
                mediaTitle={downloadPaywall.video?.title || downloadPaywall.video?.original_filename || 'Unknown Video'}
                mediaType="video"
                uploadId={downloadPaywall.video?.id}
            />
            
            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'video' })}
                contentType={signupInvite.contentType}
            />

            <AccessRequiredModal
                isOpen={accessModal.isOpen}
                onClose={() => setAccessModal({ isOpen: false, video: null })}
                accessLevel={accessModal.video?.access_level}
                action="view"
                isAuthenticated={isAuthenticated}
                viewUrl={accessModal.video ? `/view/video/${accessModal.video.username}/${accessModal.video.stored_filename || accessModal.video.original_filename || accessModal.video.id}` : null}
            />
        </div>
    );
};

export default LatestDeepWaveVideos;